import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    acceptDocuments,
    loginV2,
    selectOrganizationByLogin,
    sendActivationLink,
    setUpResetPassword,
} from "../user/services/store/authentication.actions";
import { authenticationReducerActions } from "../_services/store";
import { Button, InputGroup, InputGroupText, Modal, ModalBody, ModalHeader } from "reactstrap";
import { LoadingSpinnerButton, PathwayLinkButton } from "../_UI/Components/PathwayButtons";
import styled from "styled-components";
import { TwoButtonModalFooter } from "../_UI/StyledComponents/PathwayModal";
import PathwayTextInputGroup from "../_UI/Components/Inputs/PathwayTextInputGroup";
import PathwaySelectInputGroup from "../_UI/Components/Inputs/PathwaySelectInputGroup";
import PathwaySwitchInputGroup from "../_UI/Components/Inputs/PathwaySwitchInputGroup";
import { Paragraph } from "../_UI/StyledComponents/StyledComponents";
import { PathwayFormGridSmall } from "../_UI/StyledComponents/PathwayForm";
import { ErrorMessage, SuccessMessage, WarningMessage } from "../_UI/Components/Alerts/PathwayAlertMessages";
import stringUtils from "../_services/string-utils";
import useAcceptedDocuments from "../_hooks/useAcceptedDocuments";

const initialLoginUser = {
    email: "",
    password: "",
    selectedOrganization: null,
};

const LoginForm = (props) => {
    const dispatch = useDispatch();
    const authentication = useSelector((state) => state.authentication);

    const [loginUser, setLoginUser] = useState(initialLoginUser);

    const resetPassword = (e) => {
        e.preventDefault();
        dispatch(setUpResetPassword(authentication.resetMail));
    };

    const toggleForgetPasswordModal = () => {
        dispatch(authenticationReducerActions.setResetSuccessMessage(null));
        dispatch(authenticationReducerActions.setResetMail(null));
        dispatch(authenticationReducerActions.toggleForgetPasswordModal());
    };

    const handleLoginUserChange = (e) => {
        const { name, value } = e.target;
        setLoginUser({
            ...loginUser,
            [name]: value,
        });
    };

    const handleResetMailChange = (e) => {
        const { value } = e.target;
        dispatch(authenticationReducerActions.setResetMail(value));
    };

    const { activeTosVersionAccepted, activeDpaVersionAccepted } = useAcceptedDocuments(authentication.user);

    const handleLoginSubmit = (e) => {
        e.preventDefault();
        dispatch(authenticationReducerActions.toggleSubmitted());
        dispatch(authenticationReducerActions.setLoginErrorMessage(null));

        if (
            authentication &&
            authentication.user &&
            authentication.user.externalUUID &&
            (!activeTosVersionAccepted || !activeDpaVersionAccepted)
        ) {
            dispatch(
                acceptDocuments(authentication.acceptedTos, authentication.acceptedDpa, authentication.user, loginUser, props.redirectUrl)
            );
            if (props.loginCallback) {
                props.loginCallback();
            }
        } else {
            if (loginUser.selectedOrganization) {
                dispatch(selectOrganizationByLogin(loginUser.selectedOrganization, props.redirectUrl,loginUser.emailVerificationCode));
                if (!props.redirectUrl) {
                    setLoginUser({
                        ...loginUser,
                        selectedOrganization: null,
                    });
                }
                if (props.loginCallback) {
                    props.loginCallback();
                }
            } else if (loginUser.email && loginUser.password) {
                dispatch(loginV2(loginUser.email, loginUser.password, loginUser.selectedOrganization, props.redirectUrl));
            }
        }
    };

    const submitSendActivationLink = () => {
        dispatch(sendActivationLink(loginUser.email));
    };

    const handleTosChange = () => {
        dispatch(authenticationReducerActions.toggleTos());
    };

    const handleDpaChange = () => {
        dispatch(authenticationReducerActions.toggleDpa());
    };

    const notAllowedPasswordCharacters = useMemo(() => {
        if (loginUser == null || loginUser.password === "") {
            return false;
        }
        const regex = /^[a-zA-Z0-9_*!?@.]+$/;
        return !regex.test(loginUser.password);
    }, [loginUser]);

    const userNotLoggedInButFilledCredentials = useMemo(() => {
        return (
            !authentication.user && !stringUtils.isBlank(loginUser.email) && !stringUtils.isBlank(loginUser.password)
        );
    }, [authentication.user, loginUser]);

    const userWithAcceptedDocuments = useMemo(() => {
        return (
            authentication.user &&
            !authentication.user.acceptedTosAt &&
            authentication.acceptedTos &&
            !authentication.user.acceptedDpaAt &&
            authentication.acceptedDpa
        );
    }, [authentication]);

    const userWithNoSelectedButChosenOrganization = useMemo(() => {
        return authentication.user && !authentication.user.selectedOrganization && loginUser.selectedOrganization && loginUser.selectedOrganization!=='Bitte Firma auswählen';
    }, [authentication.user, loginUser]);

    const isLoginEnabled = useMemo(() => {
        return (
            userNotLoggedInButFilledCredentials || userWithAcceptedDocuments || userWithNoSelectedButChosenOrganization
        );
    }, [userNotLoggedInButFilledCredentials, userWithAcceptedDocuments, userWithNoSelectedButChosenOrganization]);

    return (
        <>
            <Modal isOpen={authentication.forgetPasswordModal} toggle={toggleForgetPasswordModal}>
                <ModalHeader toggle={toggleForgetPasswordModal}>Passwort vergessen</ModalHeader>
                <PasswordResetLayout>
                    <PathwayTextInputGroup
                        label="E-Mail"
                        placeHolder="E-Mail"
                        name="resetMail"
                        handleChange={handleResetMailChange}
                        value={authentication.resetMail}
                        autoFocus
                    />
                    <ErrorMessage marginless>{authentication.resetErrorMessage}</ErrorMessage>
                    <SuccessMessage marginless>{authentication.resetSuccessMessage}</SuccessMessage>
                </PasswordResetLayout>
                <ForgotPasswordFooter>
                    <LoadingSpinnerButton onClick={resetPassword} loading={!!authentication.resetLoading}>
                        Passwort zurücksetzen
                    </LoadingSpinnerButton>
                    <Button color="secondary" onClick={toggleForgetPasswordModal}>
                        {authentication.resetMail ? "Abbrechen" : "Schließen"}
                    </Button>
                </ForgotPasswordFooter>
            </Modal>
            <LoginContent>
                <InputGroupRow>
                    <InputGroupText>
                        <i className="icon-user"></i>
                    </InputGroupText>

                    <PathwayTextInputGroup
                        type="mail"
                        name="email"
                        placeHolder="E-Mail"
                        dataCy="login-input-email"
                        value={loginUser.email}
                        handleChange={handleLoginUserChange}
                        invalidInputMessage="Bitte E-Mail Adresse eingeben"
                        submitted={authentication.submitted}
                        mandatory
                        autoFocus
                    />
                </InputGroupRow>
                <InputGroupRow>
                    <InputGroupText>
                        <i className="icon-lock"></i>
                    </InputGroupText>

                    <PathwayTextInputGroup
                        type="password"
                        name="password"
                        placeHolder="Passwort"
                        dataCy="input-password"
                        handleChange={handleLoginUserChange}
                        value={loginUser.password}
                        invalidInputMessage="Bitte das Passwort eingeben"
                        submitted={authentication.submitted}
                        mandatory
                    />
                </InputGroupRow>
                {notAllowedPasswordCharacters && (
                    <WarningMessage>
                        Aufgrund einer Änderung in unserem Login sind nur noch gewisse Sonderzeichen in deinem Passwort
                        erlaubt. Dein aktuelles Passwort muss daher zurückgesetzt werden. Bitte klicke dazu auf
                        "Passwort vergessen" und setze ein neues Passwort.
                    </WarningMessage>
                )}
                {authentication && (
                    <>
                        {authentication.user &&
                            authentication.user.externalUUID &&
                            (!activeTosVersionAccepted || !activeDpaVersionAccepted) && (
                                <>
                                    <PathwayFormGridSmall>
                                        <PathwaySwitchInputGroup
                                            name="acceptedTos"
                                            dataCy="accepted-tos"
                                            onChange={handleTosChange}
                                            value={authentication.acceptedTos}
                                            label={
                                                <Paragraph>
                                                    Ich stimme der derzeit gültigen Fassung der{" "}
                                                    <a
                                                        href={
                                                            "https://app.pathway-solutions.de/Nutzungsvereinbarung_Platform.pdf"
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Nutzungsvereinbarung
                                                    </a>{" "}
                                                    zu.
                                                </Paragraph>
                                            }
                                        />
                                        <PathwaySwitchInputGroup
                                            name="acceptedDpa"
                                            dataCy="accepted-dpa"
                                            onChange={handleDpaChange}
                                            value={authentication.acceptedDpa}
                                            label={
                                                <Paragraph>
                                                    Ich stimme dem Vertrag zur{" "}
                                                    <a
                                                        href={
                                                            "https://www.pathway-solutions.de/data-privacy-webapp"
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Auftragsverarbeitung
                                                    </a>{" "}
                                                    zu.
                                                </Paragraph>
                                            }
                                        />
                                    </PathwayFormGridSmall>

                                    <ErrorMessage marginless>{authentication.acceptDocumentsErrorMessage}</ErrorMessage>
                                    <ErrorMessage marginless>{authentication.loginErrorMessage}</ErrorMessage>
                                </>
                            )}

                        {authentication.successMessage && (
                            <SuccessMessage marginless>{authentication.successMessage}</SuccessMessage>
                        )}

                        {authentication.errorMessage && (
                            <>
                                {authentication.errorMessage !== "Dieser Benutzer ist noch nicht aktiviert." ? (
                                    <ErrorMessage marginless>{authentication.errorMessage}</ErrorMessage>
                                ) : (
                                    <ErrorMessage marginless>
                                        {authentication.errorMessage}
                                        <br />
                                        <br />
                                        <button className="btn btn-md btn-primary" onClick={submitSendActivationLink}>
                                            Aktivierungslink erneut senden
                                        </button>
                                    </ErrorMessage>
                                )}
                            </>
                        )}
                        {authentication.user &&
                            !authentication.user.selectedOrganization &&
                            authentication.user.availableOrganizations &&
                            authentication.user.needsEmailCodeVerification &&
                            <>
                            Aus Sicherheitsgründen haben wir dir einen Verifizierungscode an deine E-Mail-Adresse geschickt. <br/>
                            Bitte gib den Code hier ein, um dich anzumelden.
                            <PathwayTextInputGroup
                                type="password"
                                name="emailVerificationCode"
                                placeHolder="Email Code"
                                dataCy="input-email-code"
                                handleChange={handleLoginUserChange}
                                value={loginUser.emailVerificationCode}
                                invalidInputMessage=""
                                submitted={authentication.submitted}
                                mandatory
                            />
                            </>
                        }

                        {authentication.user &&
                            !authentication.user.selectedOrganization &&
                            authentication.user.availableOrganizations &&
                            activeTosVersionAccepted &&
                            activeDpaVersionAccepted && (
                                <>
                                    {props.loginIdentifier === "shopify" ? (
                                        <div>
                                            Wähle den Account aus der mit diesem Shop verbunden werden soll:
                                            <br />
                                        </div>
                                    ) : (
                                        <PathwaySelectInputGroup
                                            name="selectedOrganization"
                                            handleChange={handleLoginUserChange}
                                            dataCy="selected-organization"
                                            value={loginUser.selectedOrganization || ""}
                                        >
                                            <option>Bitte Firma auswählen</option>
                                            {authentication.user.availableOrganizations.map((company, index) => (
                                                <option key={index} value={company.externalUUID}>
                                                    {company.name}
                                                </option>
                                            ))}
                                        </PathwaySelectInputGroup>
                                    )}
                                </>
                            )}
                    </>
                )}

                <LoginButtonArea>
                    <LoadingSpinnerButton
                        loading={!!(authentication.loggingIn || authentication.acceptDocumentsLoading)}
                        disabled={!isLoginEnabled}
                        onClick={handleLoginSubmit}
                        buttonCyData={"login-button"}
                    >
                        Login
                    </LoadingSpinnerButton>
                     
                    <PathwayLinkButton onClick={toggleForgetPasswordModal}>Passwort vergessen?</PathwayLinkButton>
                </LoginButtonArea>
            </LoginContent>
        </>
    );
};

export default LoginForm;

const LoginButtonArea = styled.div`
    display: grid;
    grid-template-columns: 0.5fr max-content;
    justify-content: space-between;
`;

const PasswordResetLayout = styled(ModalBody)`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    row-gap: var(--gap-l);
`;

const ForgotPasswordFooter = styled(TwoButtonModalFooter)`
    grid-template-columns: 0.7fr 0.3fr;
`;

const LoginContent = styled.div`
    display: grid;
    row-gap: var(--gap-xxxl);
`;

const InputGroupRow = styled(InputGroup)`
    display: grid;
    grid-template-columns: max-content auto;
`;
